import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { position, borderColor, BorderColorProps } from "styled-system";

import Box from "../Box/Box";
import Typography from "../Typography/Typography";

import { SnackbarProps } from "./snackbar.type";
import { variants } from "./variants";

const PositioningContainer = styled(Box)`
  ${position}
`;

const ActionButton = styled.div`
  cursor: pointer;
  ${css({
    color: "primary-light-blue",
    fontWeight: 2,
    marginLeft: 2,
  })}
`;

const TimerProgress = styled(Box)<{
  progress: number;
  color: string;
  roundedCorners: boolean;
}>`
  display: block;
  position: relative;
  ${(props) =>
    css({
      height: "4px",
      backgroundColor: "snackbar-progress-bg",
      borderTopLeftRadius: props.roundedCorners ? 2 : 0,
      borderTopRightRadius: props.roundedCorners ? 2 : 0,
      overflow: "hidden",
      "&::after": {
        content: "''",
        top: 0,
        left: 0,
        position: "absolute",
        width: `${props.progress}%`,
        height: "4px",
        borderTopRightRadius: props.roundedCorners ? 1 : 0,
        borderBottomRightRadius: props.roundedCorners ? 1 : 0,
        backgroundColor: props.color,
        transition: "width 1s linear",
      },
    })}
`;

const SnackBarContent = styled(Box)<
  BorderColorProps & { roundedCorners: boolean }
>`
  line-height: 1.4;
  ${(props) =>
    css({
      padding: 4,
      borderWidth: "border-size-thin",
      borderStyle: "solid",
      borderBottomLeftRadius: props.roundedCorners ? 1 : 0,
      borderBottomRightRadius: props.roundedCorners ? 1 : 0,
    })}
  ${borderColor}
`;

const Snackbar = ({
  title,
  children,
  action,
  actionLabel,
  variant = "info",
  displayTime = 5000,
  paddingX = 0,
  paddingY = 0,
  actionButtonAnalytics,
  roundedCorners = true,
  titleColor,
  verticalPosition = "top",
  onClose = () => {},
  ...props
}: SnackbarProps) => {
  const [visible, setVisible] = useState<boolean>(true);
  const [timeoutId, setTimeoutId] = useState<number | undefined>(undefined);
  const [intervalProgress, setIntervalProgress] = useState<number>(0);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let id: number;
    if (containerRef.current && displayTime > 0) {
      id = window.setTimeout(() => {
        setVisible(false);
      }, displayTime);

      setTimeoutId(id);
    }
    return () => {
      clearTimeout(id);
    };
  }, [displayTime]);

  useEffect(() => {
    if (!visible) {
      onClose();
    }
  }, [onClose, visible]);

  useEffect(() => {
    let id: number;
    if (intervalProgress < displayTime) {
      id = window.setTimeout(() => {
        setIntervalProgress(intervalProgress + 1000);
      }, 1000);
    }
    return () => {
      clearInterval(id);
    };
  }, [intervalProgress, displayTime]);

  const onMouseLeave = () => {
    if (containerRef.current && displayTime > 0) {
      const id = window.setTimeout(() => {
        setVisible(false);
      }, displayTime);

      setTimeoutId(id);
    }
  };

  const onMouseEnter = () => {
    if (containerRef.current) clearTimeout(timeoutId);
  };

  const progress = (intervalProgress / (displayTime - 2000)) * 100;

  return visible ? (
    <PositioningContainer
      ref={containerRef}
      display="flex"
      justifyItems="center"
      paddingX={paddingX}
      paddingY={paddingY}
      width="100%"
      height="auto"
      role="alert"
      position="fixed"
      top={verticalPosition === "top" ? 0 : "auto"}
      bottom={verticalPosition === "bottom" ? 0 : "auto"}
      right={0}
      zIndex={1000}
      {...props}
    >
      <Box
        width="100%"
        height="auto"
        display="flex"
        flexDirection="column"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        color="footer-blue"
      >
        <TimerProgress
          progress={progress}
          color={variants[variant].progressBarColor}
          roundedCorners={roundedCorners}
        />
        <SnackBarContent
          roundedCorners={roundedCorners}
          borderColor={variants[variant].borderColor}
          backgroundColor={variants[variant].backgroundColor}
          aria-label="snackbar content"
        >
          <Box display="flex" height="auto" alignItems="center">
            <Typography
              marginX={0}
              marginY={0}
              variant="body"
              htmlTag="h6"
              color={titleColor || "text-color"}
              css={`
                flex: 1;
                font-weight: 500;
              `}
            >
              {title}
            </Typography>
            {action && actionLabel && (
              <ActionButton
                onClick={action}
                role="button"
                data-en="click"
                data-ec="snackbar"
                data-el={actionButtonAnalytics?.eventLabel}
                data-ea={actionButtonAnalytics?.eventAction}
                data-es={actionButtonAnalytics?.eventSection}
                data-et={actionButtonAnalytics?.eventType}
                data-ev={actionButtonAnalytics?.eventValue}
              >
                {actionLabel}
              </ActionButton>
            )}
          </Box>
          {children && <Box marginTop={3}>{children}</Box>}
        </SnackBarContent>
      </Box>
    </PositioningContainer>
  ) : null;
};

export default Snackbar;
